import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { CloseIcon } from "../../minimals/theme/overrides/CustomIcons";
import UploadMultiFile from "../components/upload/UploadMultiFile";
import OrderAttachments from "./OrderAttachments";
import { Attachment } from "./types/Attachment";
import { generateFullPaths } from "../taskManagement/documents/generateFullPaths";
import { toBase64 } from "../utils/helpers";
import { stripDataUriPrefix } from "../utils/stripDataUriPrefix";
import { ConcatenatedDocumentTypes, Document } from "../reports/dashboard/policyIssues/types";

export default function UploadModal({
  updateItems,
  open,
  handleClose,
  hasDocumentType,
  hasAdditionalDescription,
  validationCheck,
  error,
  onSave,
  documentInfo,
  orderNumber,
  isUseNameOfFile,
  applicationInitiator = "",
  customHeaderText = "",
  customEditFunction,
  allowMultiple = false,
  onSaveFiles
}: {
  updateItems: () => void;
  open: boolean;
  handleClose: () => void;
  hasDocumentType?: boolean;
  hasAdditionalDescription?: boolean;
  validationCheck?: boolean | null;
  error?: string;
  onSave: (documentIds: number[]) => void | Promise<void>;
  documentInfo: Document;
  orderNumber: string;
  isUseNameOfFile?: boolean;
  applicationInitiator: string;
  customHeaderText?: string;
  customEditFunction?: (base64: string, documentIds: number[]) => void;
  allowMultiple?: boolean;
  onSaveFiles?: (files: File[]) => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [concatenatedDocumentTypes, setConcatenatedDocumentTypes] = useState<ConcatenatedDocumentTypes[]>([]);
  const [selectedConcatenatedDocumentType, setSelectedConcatenatedDocumentType] =
    useState<ConcatenatedDocumentTypes | null>(null);
  const [additionalDescription, setAdditionalDescription] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([]);
  const [validationError, setValidationError] = useState(false);
  const submittingRef = useRef(false);

  const getAndSetDocumentTypes = async (orderNumber: string) => {
    const { data } = await axios.get(`/api/taskmanagement/documents/getcategories?ordernumber=${orderNumber}`);
    const concatenatedDocumentTypes = generateFullPaths(data);
    setConcatenatedDocumentTypes(concatenatedDocumentTypes);
  };

  const handleFileDrop = useCallback(
    (acceptedFiles: any[]) => {
      const mapped = acceptedFiles.map((f) =>
        Object.assign(f, { preview: URL.createObjectURL(f) })
      );
      setFiles((prev) => (allowMultiple ? [...prev, ...mapped] : [mapped[0]]));
    },
    [allowMultiple]
  );

  const saveUploadToMomentum = async (document: Document, file: File) => {
    const description = [
      selectedConcatenatedDocumentType?.description || documentInfo.description || "",
      additionalDescription
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    const { data } = await axios.post<number>("/api/outstandingChecks/UploadDocument", {
      ...document,
      mTANumber: orderNumber,
      base64File: stripDataUriPrefix(await toBase64(file)),
      fileName: isUseNameOfFile ? file?.name : (documentInfo.fileName || file?.name),
      subcategoryId: selectedConcatenatedDocumentType?.subcategoryId || documentInfo.subcategoryId,
      description: description,
      applicationInitiator
    });
    return data;
  };

  const getMergedFile = async (): Promise<File> => {
    const filePayloads = await Promise.all(
      files.map(async (f) => ({
        base64File: stripDataUriPrefix(await toBase64(f)),
        fileName: f.name
      }))
    );
    const { data: mergedBase64 } = await axios.post<string>("/api/outstandingChecks/MergePdfs", {
      files: filePayloads
    });
    const bytes = Uint8Array.from(atob(mergedBase64), (c) => c.charCodeAt(0));
    return new File([bytes], files[0].name, { type: "application/pdf" });
  };

  const save = async () => {
    if (submittingRef.current) {
      return;
    }

    if (validationCheck) {
      setValidationError(true);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (customEditFunction !== undefined && files[0]) {
        const base64File = stripDataUriPrefix(await toBase64(files[0]));
        await customEditFunction(base64File, []);
        updateItems();
        handleClose();
        return;
      }

      const attachmentIds = selectedAttachments.map((d) => d.id);

      if (onSaveFiles && files.length > 0 && (!hasDocumentType || selectedConcatenatedDocumentType)) {
        const filesToSave = allowMultiple && files.length > 1 ? [await getMergedFile()] : files;
        await onSaveFiles(filesToSave);
        if (attachmentIds.length) {
          await onSave(attachmentIds);
        }
      } else {
        const uploadedIds =
          files.length > 0 && (!hasDocumentType || selectedConcatenatedDocumentType)
            ? allowMultiple && files.length > 1
              ? [await saveUploadToMomentum(documentInfo, await getMergedFile())]
              : await Promise.all(files.map((file) => saveUploadToMomentum(documentInfo, file)))
            : [];

        const allIds = [...attachmentIds, ...uploadedIds];
        if (!allIds.length) {
          return;
        }

        await onSave(allIds);
      }

      updateItems();
      handleClose();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (hasDocumentType && orderNumber) {
      getAndSetDocumentTypes(orderNumber);
    }
  }, [orderNumber]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        <Stack direction="column">
          <Typography variant="subtitle1">{customHeaderText || "Upload"}</Typography>
        </Stack>
      </DialogTitle>
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingBottom: 3 }}>
        <Stack direction="row" spacing={2} paddingTop={1}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Stack direction="column" justifyContent="space-between" spacing={2}>
                {hasDocumentType && (
                  <Autocomplete
                    fullWidth
                    onChange={(e, value) => setSelectedConcatenatedDocumentType(value)}
                    options={concatenatedDocumentTypes}
                    getOptionLabel={(option) => option.fullPath || ""}
                    renderInput={(params) => (
                      <TextField {...params} label="Document Type" placeholder="Start typing..." />
                    )}
                  />
                )}
                {hasAdditionalDescription && (
                  <TextField
                    label="Additional Description"
                    onChange={(e) => setAdditionalDescription(e.target.value)}
                  />
                )}
                <UploadMultiFile
                  files={files}
                  onDrop={handleFileDrop}
                  onRemove={(file) => setFiles((prev) => prev.filter((f) => f !== file))}
                  onRemoveAll={() => setFiles([])}
                  showPreview={false}
                  showSelectedFiles={true}
                  multiple={true}
                />
                {allowMultiple && files.length > 1 && (
                  <Alert severity="warning">
                    Multiple files will be merged into a single document before uploading.
                  </Alert>
                )}
                {selectedConcatenatedDocumentType && (
                  <Stack direction="column" justifyContent="right">
                    <Box>
                      <Typography variant="body2">Uploaded document will be saved to</Typography>
                      <Typography variant="subtitle2">{`${selectedConcatenatedDocumentType.fullPath} ${
                        additionalDescription ? `- ${additionalDescription}` : ""
                      }`}</Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Typography variant="body2" marginBottom={1}>
                Or <strong>Choose a document from Momentum</strong>
              </Typography>
              <OrderAttachments
                selectedAttachments={selectedAttachments}
                setSelectedAttachments={setSelectedAttachments}
                orderNumber={orderNumber}
                showUploadDate
              />
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
      <DialogActions>
        {files.length > 0 && selectedAttachments.length > 0 && (
          <Typography color="error" variant="body2" sx={{ mr: "auto" }}>
            Please remove the uploaded file or deselect the Momentum document — not both.
          </Typography>
        )}
        <Button variant="outlined" onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            submitting ||
            (files.length === 0 && selectedAttachments.length === 0) ||
            (files.length > 0 && selectedAttachments.length > 0)
          }
          onClick={save}
        >
          {submitting ? "Saving...." : "Save"}
        </Button>
        {validationError && <Typography color="red">{error}</Typography>}
      </DialogActions>
    </Dialog>
  );
}
